import React from "react";
import {BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid} from "recharts";
import {CircleHelp, Download, ChevronDown} from "lucide-react";
import {Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {useIsMobile} from "@/hooks/use-mobile";
import {ChartContainer} from "@/components/ui/chart";
import {cn} from "@/lib/utils";
import axiosInstance from "@/api/axios";
import {useTranslation} from "react-i18next";
import {Button} from "@/components/ui/button";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "@/components/ui/dropdown-menu";
import {AiOutlineArrowDown} from 'react-icons/ai'
import {AiOutlineArrowUp} from 'react-icons/ai'

export const SubclassDisplayChart = React.forwardRef(({
                                                          className,
                                                          id,
                                                          name,
                                                          wasteitem,
                                                          dayvalue,
                                                          datevalue = [{
                                                              startDate: null,
                                                              endDate: null,
                                                              key: 'selection'
                                                          }],
                                                          totalwasteproduceddata,
                                                          wasteweight,
                                                          colorwastechart,
                                                          percentagechangewastechart,
                                                          isCustomDateRange,
                                                          ...props
                                                      }, ref) => {
    const {t} = useTranslation();
    const isMobile = useIsMobile();
    const [processedData, setProcessedData] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [hasData, setHasData] = React.useState(false);
    
    // Internal ref for the chart container (for PNG export)
    const internalChartRef = React.useRef(null);
    
    // Combine forwarded ref and internal ref
    React.useImperativeHandle(ref, () => internalChartRef.current);

    const chartTitle = t('subwastecompositionlabel');
    const [wasteTypeWeights, setWasteTypeWeights] = React.useState({});

    const shouldFilterWasteType = (wasteType) => {
        if (!wasteType) return true;
        const normalized = wasteType.toLowerCase().trim();
        return normalized === 'nothing' || normalized === '';
    };

    const normalizeAndCombineWasteTypes = (name) => {
        if (!name) return '';
        const normalized = name.toLowerCase().replace(/[_-]/g, '').trim();

        if (normalized === 'nothing') return '';

        const combinedMap = {
            'pmd': 'PMD',
            'residual': t('residuallabel') || 'Residual',
            'rest': t('residuallabel') || 'Residual',
            'papercardboard': t('paperlabel') || 'Paper/Cardboard',
            'organic': t('organiclabel') || 'Organic',
            'glass': 'Glass'
        };

        return combinedMap[normalized] ||
            name.replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
                .replace(/[_-]/g, ' ')
                .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
                .replace(/\s+/g, ' ')
                .trim();
    };

    const normalizeAndFormatSubclassName = (name) => {
        if (!name) return '';

        const transformations = {
            'plasticbottle': t('plasticBottle'),
            'metalcan': t('metalCan'),
            'plasticbag': t('plasticBag'),
            'plasticbagcolor': t('plasticBagColor'),
            'organicmandarin': t('organicMandarin'),
            'paperbox': t('paperBox'),
            'papercarton': t('paperCarton'),
            'papercolor': t('paperColor'),
            'papertissue': t('paperTissue'),
            'paperoffice': t('paperOffice'),
            'plasticbagwrapper': t('plasticBagWrapper'),
            'plasticcontainer': t('plasticContainer'),
            'drinkcarton': t('drinkCarton'),
            'juicepackage': t('juicePackage'),
            'rubbergloves': t('rubberGloves'),
            'banana': t('banana'),
            'apple': t('apple'),
            'papercoffeecup': t('paperCoffeeCup'),
            'plasticbagsilver': t('plasticBagSilver')
        };

        const lowerName = name.toLowerCase().replace(/[_-]/g, '');
        return transformations[lowerName] ||
            name.replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
                .replace(/[_-]/g, ' ')
                .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
                .replace(/\s+/g, ' ')
                .trim();
    };

    const getSubclassColor = (name) => {
        if (!name) return '#CCCCCC';

        const nameForHash = name.toLowerCase().replace(/\s+/g, '');

        const hashCode = (str) => {
            let hash = 0;
            let i;
            for (i = 0; i < str.length; i++) {
                hash = str.charCodeAt(i) + ((hash << 5) - hash);
            }
            return hash;
        };

        const baseColor = hashCode(nameForHash.substring(0, 6)) & 0xFFFFFF;
        let hexColor = '#' + baseColor.toString(16).padStart(6, '0');

        if (nameForHash.length > 6) {
            const variation = hashCode(nameForHash.substring(6)) & 0x7F;
            const rgb = parseInt(hexColor.slice(1), 16);

            const r = Math.min(255, ((rgb >> 16) & 0xFF) + (variation % 3) * 30);
            const g = Math.min(255, ((rgb >> 8) & 0xFF) + (variation % 5) * 15);
            const b = Math.min(255, ((rgb >> 0) & 0xFF) + (variation % 7) * 10);

            hexColor = `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
        }

        const rgb = parseInt(hexColor.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >> 8) & 0xff;
        const b = (rgb >> 0) & 0xff;
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;

        if (brightness > 220) {
            return `#${[
                Math.floor(r * 0.8).toString(16).padStart(2, '0'),
                Math.floor(g * 0.8).toString(16).padStart(2, '0'),
                Math.floor(b * 0.8).toString(16).padStart(2, '0')
            ].join('')}`;
        } else if (brightness < 60) {
            return `#${[
                Math.min(255, Math.floor(r * 1.4)).toString(16).padStart(2, '0'),
                Math.min(255, Math.floor(g * 1.4)).toString(16).padStart(2, '0'),
                Math.min(255, Math.floor(b * 1.4)).toString(16).padStart(2, '0')
            ].join('')}`;
        }

        return hexColor;
    };

    const chartConfig = React.useMemo(() => {
        const config = {};

        if (processedData.length > 0 && hasData) {
            Object.keys(processedData[0]).forEach((key) => {
                if (key !== 'name') {
                    const displayName = normalizeAndFormatSubclassName(key);
                    config[key] = {
                        label: displayName,
                        color: getSubclassColor(displayName),
                        originalKey: key,
                    };
                }
            });
        }

        return config;
    }, [processedData, hasData, t]);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                setHasData(false);

                let binIdentifiers;
                if (Array.isArray(id)) {
                    binIdentifiers = id;
                } else if (typeof id === 'string' && id.includes(',')) {
                    binIdentifiers = id.split(',');
                } else {
                    binIdentifiers = [id];
                }

                binIdentifiers = binIdentifiers
                    .map(id => typeof id === 'string' ? id.trim() : String(id))
                    .filter(id => id.length > 0);

                if (binIdentifiers.length === 0) {
                    throw new Error('No valid bin identifiers provided');
                }

                const binResponse = await axiosInstance.get('/get_bins');
                const validBins = binIdentifiers.map(identifier => {
                    let bin = binResponse.data.find(b =>
                        String(b.unique_identifier).toLowerCase() === String(identifier).toLowerCase()
                    );

                    if (!bin && !isNaN(identifier)) {
                        bin = binResponse.data.find(b => b.id === parseInt(identifier));
                    }

                    return bin;
                }).filter(bin => bin !== undefined && bin !== null);

                if (validBins.length === 0) {
                    throw new Error(
                        `No bins found matching identifiers: ${binIdentifiers.join(', ')}`
                    );
                }

                const binIds = validBins.map(bin => bin.id).join(',');

                let apiUrl = `/subclass_waste_composition/${binIds}/${dayvalue.toLowerCase()}/`;
                const queryParams = new URLSearchParams();

                // SAFE DATE HANDLING
                if (isCustomDateRange && datevalue[0] && datevalue[0].startDate && datevalue[0].endDate) {
                    const rawStartDate = datevalue[0].startDate;
                    const rawEndDate = datevalue[0].endDate;
                    let startDateStr, endDateStr;

                    if (rawStartDate instanceof Date) {
                        startDateStr = rawStartDate.toISOString();
                    } else if (typeof rawStartDate === 'string') {
                        startDateStr = rawStartDate;
                    } else {
                        startDateStr = new Date(rawStartDate).toISOString();
                    }

                    if (rawEndDate instanceof Date) {
                        endDateStr = rawEndDate.toISOString();
                    } else if (typeof rawEndDate === 'string') {
                        endDateStr = rawEndDate;
                    } else {
                        endDateStr = new Date(rawEndDate).toISOString();
                    }

                    queryParams.append('start_date', startDateStr);
                    queryParams.append('end_date', endDateStr);
                    console.log('SubclassChart: Using custom date range', startDateStr, endDateStr);
                }

                if (queryParams.toString()) {
                    apiUrl += `?${queryParams.toString()}`;
                }

                console.log('SubclassChart: Fetching data from', apiUrl);
                const response = await axiosInstance.get(apiUrl);

                if (!response.data || Object.keys(response.data).length === 0) {
                    setProcessedData([]);
                    setCategories([]);
                    setHasData(false);
                    return;
                }

                const combinedData = {};
                Object.keys(response.data).forEach(wasteType => {
                    if (shouldFilterWasteType(wasteType)) return;

                    const normalizedType = normalizeAndCombineWasteTypes(wasteType);

                    if (!normalizedType) return;

                    if (!combinedData[normalizedType]) {
                        combinedData[normalizedType] = {};
                    }

                    Object.keys(response.data[wasteType]).forEach(subClass => {
                        if (shouldFilterWasteType(subClass)) return;

                        const normalizedSubClass = normalizeAndFormatSubclassName(subClass);
                        const value = response.data[wasteType][subClass];

                        if (normalizedSubClass && value > 0) {
                            combinedData[normalizedType][normalizedSubClass] =
                                (combinedData[normalizedType][normalizedSubClass] || 0) + value;
                        }
                    });
                });

                const filteredCombinedData = {};
                Object.keys(combinedData).forEach(type => {
                    const typeData = combinedData[type];
                    const hasValidSubclasses = Object.keys(typeData).length > 0 &&
                        Object.values(typeData).some(val => val > 0);

                    if (hasValidSubclasses) {
                        filteredCombinedData[type] = typeData;
                    }
                });

                const wasteTypes = Object.keys(filteredCombinedData);
                const subClasses = Array.from(
                    new Set(wasteTypes.flatMap(type => Object.keys(filteredCombinedData[type] || {})))
                );

                const formattedData = wasteTypes.map(type => {
                    const typeData = filteredCombinedData[type] || {};
                    const total = Object.values(typeData).reduce((sum, val) => sum + val, 0);

                    if (total === 0) return null;

                  const subClassValues = {};
const entries = Object.keys(typeData);

// Store raw unrounded percentages
entries.forEach((subClass) => {
    subClassValues[subClass] = total > 0 ? (typeData[subClass] / total) * 100 : 0;
});

// Force last item to make sum exactly 100 with no rounding drift
const assignedTotal = entries.slice(0, -1).reduce((sum, key) => sum + subClassValues[key], 0);
subClassValues[entries[entries.length - 1]] = 100 - assignedTotal;

                    return {
                        name: type,
                        ...subClasses.reduce((acc, subClass) => ({
                            ...acc,
                            [subClass]: subClassValues[subClass] || 0
                        }), {})
                    };
                }).filter(item => item !== null);

                if (formattedData.length > 0) {
                    setProcessedData(formattedData);
                    setCategories(wasteTypes);
                    setHasData(true);
                } else {
                    setProcessedData([]);
                    setCategories([]);
                    setHasData(false);
                }

            } catch (err) {
                console.error('Data fetch error:', err);
                if (err.response?.status === 404 || err.message?.includes('No waste data found')) {
                    setProcessedData([]);
                    setCategories([]);
                    setHasData(false);
                    setError(null);
                } else {
                    setProcessedData([]);
                    setCategories([]);
                    setHasData(false);
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dayvalue, id, t, isCustomDateRange, datevalue]);

    React.useEffect(() => {
        const fetchWasteTypeWeights = async () => {
            try {
                let binIdentifiers;
                if (Array.isArray(id)) {
                    binIdentifiers = id;
                } else if (typeof id === 'string' && id.includes(',')) {
                    binIdentifiers = id.split(',');
                } else {
                    binIdentifiers = [id];
                }

                const bins = binIdentifiers.map(b => String(b).trim()).join(',');

                const timeRangeMap = {
                    'day': 'Day',
                    'week': 'Week',
                    'month': 'Month',
                    '3months': '3Month',
                    '6months': '6Month',
                    'year': 'Year'
                };

                const normalizedDayValue = dayvalue.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
                const timeRange = timeRangeMap[normalizedDayValue] || 'Month';

                const currentMonth = new Date().getMonth() + 1;
                let season = 'summer';
                if (currentMonth >= 3 && currentMonth <= 5) season = 'spring';
                else if (currentMonth >= 6 && currentMonth <= 8) season = 'summer';
                else if (currentMonth >= 9 && currentMonth <= 11) season = 'autumn';
                else season = 'winter';

                const params = {
                    bins: bins,
                    timeRange: timeRange,
                    season: season
                };

                // SAFE DATE HANDLING
                if (isCustomDateRange && datevalue[0] && datevalue[0].startDate && datevalue[0].endDate) {
                    const rawStartDate = datevalue[0].startDate;
                    const rawEndDate = datevalue[0].endDate;

                    if (rawStartDate instanceof Date) {
                        params.start_date = rawStartDate.toISOString();
                    } else if (typeof rawStartDate === 'string') {
                        params.start_date = rawStartDate;
                    } else {
                        params.start_date = new Date(rawStartDate).toISOString();
                    }

                    if (rawEndDate instanceof Date) {
                        params.end_date = rawEndDate.toISOString();
                    } else if (typeof rawEndDate === 'string') {
                        params.end_date = rawEndDate;
                    } else {
                        params.end_date = new Date(rawEndDate).toISOString();
                    }
                }

                const response = await axiosInstance.get('/others_page_waste_composition', {
                    params: params
                });

                if (response.data) {
                    const weights = {};

                    if (response.data.paper !== undefined) {
                        weights['Paper/Cardboard'] = response.data.paper;
                        weights['Paper'] = response.data.paper;
                        weights[t('paperlabel')] = response.data.paper;
                    }

                    if (response.data.pmd !== undefined) {
                        weights['PMD'] = response.data.pmd;
                    }

                    if (response.data.residual !== undefined) {
                        weights['Residual'] = response.data.residual;
                        weights['Rest'] = response.data.residual;
                        weights[t('residuallabel')] = response.data.residual;
                    }

                    if (response.data.organic !== undefined) {
                        weights['Organic'] = response.data.organic;
                        weights[t('organiclabel')] = response.data.organic;
                    }

                    if (response.data.glass !== undefined) {
                        weights['Glass'] = response.data.glass;
                    }

                    setWasteTypeWeights(weights);
                }

            } catch (error) {
                console.error('Error fetching waste type weights:', error);
                setWasteTypeWeights({});
            }
        };

        fetchWasteTypeWeights();
    }, [id, dayvalue, t, isCustomDateRange, datevalue]);

    const handleExportCSV = () => {
        if (!processedData.length || !hasData) return;

        const subclassKeys = Object.keys(chartConfig);

        const headers = ['Waste Type', 'Waste Type Weight (kg)', 'Subclass', 'Percentage (%)', 'Weight (kg)'];

        const rows = [];
        let totalKgSum = 0;

        processedData.forEach(item => {
            const wasteType = item.name;
            const wasteTypeWeight = wasteTypeWeights[wasteType] || 0;

            subclassKeys.forEach(key => {
                const percentageValue = item[key] || 0;

                if (percentageValue > 0) {
                    const kgValue = (percentageValue / 100) * wasteTypeWeight;
                    totalKgSum += kgValue;

                    rows.push([
                        wasteType,
                        wasteTypeWeight.toFixed(3).replace('.', ','),
                        chartConfig[key].label,
                        percentageValue.toFixed(2),
                        kgValue.toFixed(3).replace('.', ',')
                    ]);
                }
            });
        });

        let csvContent = '# Subclass Waste Composition Export\n';
        csvContent += `# Export Date: ${new Date().toISOString().split('T')[0]}\n`;

        if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
            const startD = datevalue[0].startDate instanceof Date ? datevalue[0].startDate : new Date(datevalue[0].startDate);
            const endD = datevalue[0].endDate instanceof Date ? datevalue[0].endDate : new Date(datevalue[0].endDate);

            csvContent += `# Date Range: ${!isNaN(startD) ? startD.toISOString().split('T')[0] : 'Invalid'} to ${!isNaN(endD) ? endD.toISOString().split('T')[0] : 'Invalid'}\n`;
        } else {
            csvContent += `# Time Period: ${dayvalue}\n`;
        }

        csvContent += `# Total Waste Weight: ${String(wasteweight).replace('.', ',')} kg\n`;
        csvContent += `# Calculated Total from Subclasses: ${totalKgSum.toFixed(3).replace('.', ',')} kg\n`;
        csvContent += `# Total Objects: ${totalwasteproduceddata}\n`;
        csvContent += '#\n';
        csvContent += '# Note: Percentages show composition within each waste type (sums to 100% per type)\n';
        csvContent += '\n';

        csvContent += headers.join(',') + '\n';
        csvContent += rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

        csvContent += '\n\n# Summary by Waste Type\n';
        csvContent += 'Waste Type,Waste Type Weight (kg),Total Percentage (%),Calculated Total Weight (kg)\n';

        processedData.forEach(item => {
            const wasteType = item.name;
            const wasteTypeWeight = wasteTypeWeights[wasteType] || 0;
            const totalPercentage = subclassKeys.reduce((sum, key) => sum + (item[key] || 0), 0);
            const totalKg = (totalPercentage / 100) * wasteTypeWeight;
            csvContent += `"${wasteType}","${wasteTypeWeight.toFixed(3).replace('.', ',')}","${totalPercentage.toFixed(2)}","${totalKg.toFixed(3).replace('.', ',')}"\n`;
        });

        csvContent += '\n\n# Contamination Analysis Helper\n';
        csvContent += '# Use this to identify misplaced items (subclasses in wrong waste type)\n';
        csvContent += 'Waste Type,Subclass,Weight (kg),Potential Issue\n';

        processedData.forEach(item => {
            const wasteType = item.name;
            const wasteTypeWeight = wasteTypeWeights[wasteType] || 0;

            subclassKeys.forEach(key => {
                const percentageValue = item[key] || 0;
                if (percentageValue > 0) {
                    const kgValue = (percentageValue / 100) * wasteTypeWeight;
                    const subclassName = chartConfig[key].label.toLowerCase();
                    const wasteTypeName = wasteType.toLowerCase();

                    let issue = 'Correct placement';
                    if (wasteTypeName.includes('organic') && !subclassName.includes('organic') && !subclassName.includes('banana') && !subclassName.includes('apple') && !subclassName.includes('mandarin')) {
                        issue = 'POSSIBLE CONTAMINATION';
                    } else if (wasteTypeName.includes('pmd') && (subclassName.includes('paper') || subclassName.includes('organic'))) {
                        issue = 'POSSIBLE CONTAMINATION';
                    } else if (wasteTypeName.includes('paper') && !subclassName.includes('paper') && !subclassName.includes('cardboard') && !subclassName.includes('box') && !subclassName.includes('carton')) {
                        issue = 'POSSIBLE CONTAMINATION';
                    }

                    csvContent += `"${wasteType}","${chartConfig[key].label}","${kgValue.toFixed(3).replace('.', ',')}","${issue}"\n`;
                }
            });
        });

        const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `subclass_waste_composition_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleExportPNG = () => {
        if (!internalChartRef.current) return;

        const controls = internalChartRef.current.querySelector('.export-controls');
        if (controls) controls.style.display = 'none';

        import('html2canvas').then((html2canvas) => {
            html2canvas.default(internalChartRef.current, {
                scale: 2,
                logging: false,
                useCORS: true,
                backgroundColor: "#FFFFFF",
            }).then((canvas) => {
                const link = document.createElement('a');
                link.download = `subclass_waste_composition_${new Date().toISOString().split('T')[0]}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
            }).finally(() => {
                if (controls) controls.style.display = '';
            });
        }).catch(err => {
            console.error('Failed to export as PNG:', err);
            if (controls) controls.style.display = '';
        });
    };

    return (
        <div
            ref={internalChartRef}
            className={cn(
                "lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section",
                className
            )}
            {...props}
        >
            {/* Header Section - Always visible to maintain height */}
            <div className="relative">
                <div className="flex justify-between items-center pb-1">
                    <div>
                        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                            {chartTitle}
                        </h4>
                        {isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate && (
                            <p className="text-sm text-muted-foreground mt-1">
                                {(() => {
                                    const sDate = datevalue[0].startDate instanceof Date ? datevalue[0].startDate : new Date(datevalue[0].startDate);
                                    const eDate = datevalue[0].endDate instanceof Date ? datevalue[0].endDate : new Date(datevalue[0].endDate);
                                    if (!isNaN(sDate) && !isNaN(eDate)) {
                                        return `${sDate.toLocaleDateString()} - ${eDate.toLocaleDateString()}`;
                                    }
                                    return '';
                                })()}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center space-x-1 export-controls">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-primary"
                                    disabled={!hasData || loading}
                                >
                                    <Download className="h-4 w-4 mr-1"/>
                                    <span className="text-sm">{t('export')}</span>
                                    <ChevronDown className="h-4 w-4 ml-1"/>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={handleExportCSV}>
                                    {t('downloadAsCSV')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleExportPNG}>
                                    {t('downloadAsPNG')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <TooltipProvider>
                            <UITooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground hover:text-primary h-8 w-8 p-0"
                                    >
                                        <CircleHelp className="h-4 w-4"/>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                    <p>
                                        This indicates the individual composition of your waste.<br/>
                                        Eg. Plastic bottles within PMD.<br/>
                                        If sub-classes do not match the main waste stream, this is a contamination
                                        event.<br/>
                                        <br/>
                                        <strong>Date Range:</strong> Use the calendar in the header to select a custom
                                        date range
                                    </p>
                                </TooltipContent>
                            </UITooltip>
                        </TooltipProvider>
                    </div>
                </div>

                <div className="flex items-center justify-center gap-2 mb-1 text-sm min-h-[1.5rem]">
                    {hasData && totalwasteproduceddata > 0 && wasteweight > 0 && !loading && !error ? (
                        <>
                            <span className="font-semibold">
                                {totalwasteproduceddata} <span className="font-light">{t('objectslabel')}</span> | {String(wasteweight).replace('.', ',')}
                                <span className="font-light"> KG</span>
                            </span>
                            {percentagechangewastechart && percentagechangewastechart !== 0 && (
                                <span
                                    className={`text-${colorwastechart}-500 flex items-center justify-center text-xs font-semibold`}>
                                    {percentagechangewastechart}% {colorwastechart === "red" ?
                                    <AiOutlineArrowUp className="h-3 w-3"/> :
                                    <AiOutlineArrowDown className="h-3 w-3"/>}
                                </span>
                            )}
                        </>
                    ) : !hasData && !loading && !error ? (
                        <span className="font-semibold text-muted-foreground">
                            {t('noData')}
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Content Area - Fixed height to ensure stability */}
            <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{width: '100%', height: '94%'}}>
                {loading ? (
                    <div className="flex items-center justify-center h-full w-full">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-full">
                        <div className="text-muted-foreground text-center">
                            <p className="mb-2 text-lg">{t('errorLoadingData')}</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                ) : (
                    <ChartContainer config={chartConfig} className="h-full w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={hasData ? processedData : []}
                                layout="vertical"
                                margin={{top: 1, right: 70, left: 10, bottom: 20}}
                                barSize={20}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false}/>
                                <XAxis
                                    type="number"
                                    domain={[0, 100]}
                                    tickFormatter={(value) => `${value.toFixed(1)}%`}
                                />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    width={60}
                                    tick={{fontSize: 12}}
                                    axisLine={false}
                                    tickLine={false}
                                    tickMargin={10}
                                />
                                <Tooltip
                                    content={({active, payload, label}) => {
                                        if (!active || !payload || !hasData) return null;

                                        const filteredAndSortedPayload = payload
                                            .filter(item => item.value > 0)
                                            .map(item => ({
                                                ...item,
                                                color: chartConfig[item.dataKey]?.color || item.color,
                                                name: chartConfig[item.dataKey]?.label || item.name
                                            }))
                                            .sort((a, b) => b.value - a.value);

                                        return (
                                            <div className="rounded-lg border bg-background p-3 shadow-md">
                                                <div className="font-medium mb-2">{`${t('wastetype')}: ${label}`}</div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {filteredAndSortedPayload.map((item, index) => (
                                                        <React.Fragment key={`item-${index}`}>
                                                            <div className="flex items-center">
                                                                <div className="mr-1 h-2 w-2 rounded-full"
                                                                     style={{backgroundColor: item.color}}/>
                                                                <div>{item.name}</div>
                                                            </div>
                                                            <div className="text-right font-medium">
                                                                {item.value.toFixed(1)}%
                                                            </div>
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                                {hasData && Object.keys(chartConfig).map((key) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        stackId="a"
                                        fill={chartConfig[key].color}
                                        name={chartConfig[key].label}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                )}
            </div>
        </div>
    );
});

SubclassDisplayChart.displayName = "SubclassDisplayChart";